import React, { useEffect, useState, useCallback } from "react";
import Table from 'react-bootstrap/Table';
import Collapse from 'react-bootstrap/Collapse';
import '../../projects/scss/task.scss'
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import idx from 'idx'
import { itemFields } from "../../../constants/fields.constant";
import { updateSubTaskAction } from "../../../slice/add-task";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { getInventoryLists, getSuggestedMaterials } from "../../../actions/inventory.action";
import { warningSnackBar } from "../../../actions/common.action";
import { debounceTime, filterItems, filterManufacturer, isInlineItem, INLINE_CATALOG_DISPLAY_NAME } from "../../../util/common";
import { getFormatedUnit } from "../../../util";
import { v4 as uuidv4 } from "uuid";
import ProfitMarginLock from "../../../components/profitMarginLock-dialog";
import CustomInput, { NumberInput } from "../../../components/inputs/new-input";
import { debounce } from "@mui/material/utils";
import ReplaceMaterialModal from "./replaceMaterialModal";


const filter = createFilterOptions();
/**
 * Material Component 
 * 
 * A comprehensive and interactive component for managing material items within a sub-task.
 * Supports advanced filtering, dynamic pricing logic, catalog-based item selection, and 
 * integration with project-level profit margin settings.
 * 
 * @Modified CTBL-275 - Material list UI fix
 * 
 * MAJOR FEATURES:
 * 1. Material Resource Management:
 *    - Displays material items in a structured table format
 *    - Inline editing for unit cost and quantity
 *    - Dynamic calculation of total and sale amount
 *    - Row-level delete option
 *    - Subtotal summary row
 * 
 * 2. Add Material Flow:
 *    - Expandable section with advanced filtering capabilities
 *    - Autocomplete fields for Catalog, Category, Part No., and Item Name
 *    - Unit cost and quantity fields with live total calculation
 *    - Profit margin lock logic integrated for pricing control
 * 
 * 3. State and Redux Integration:
 *    - Uses `updateSubTaskAction` to update materials in sub-task Redux state
 *    - Maintains internal state for filters, items list, dialog state, and unit setup
 *    - Uses debounce for optimized inventory search requests
 * 
 * 4. Smart Profit Margin Handling:
 *    - Allows toggling of sale amount lock per material
 *    - Integrates `ProfitMarginLock` modal for confirmation
 *    - Updates all relevant items on lock confirmation
 * 
 * UI/UX IMPROVEMENTS:
 * 1. Table Design:
 *    - Clean Bootstrap table with responsive layout
 *    - Editable input fields with proper alignment
 *    - Conditional rendering for edit/view mode
 * 
 * 2. Interaction Enhancements:
 *    - Expandable 'Add Material' row with smooth transitions
 *    - Reusable `CustomInput` component ensures consistent behavior
 *    - Real-time feedback on item selection and value updates
 *    - Prevention of duplicate material additions
 * 
 * TECHNICAL ENHANCEMENTS:
 * 1. Performance:
 *    - Debounced API calls for fetching inventory items
 *    - Optimized filtering using utility functions (`filterItems`, `filterManufacturer`)
 *    - Efficient state updates using `lodash.cloneDeep` and immutability principles
 * 
 * 2. Extensibility:
 *    - Designed to accommodate unit list, profit margin config, category filters
 *    - Easily extendable for markup calculations, inline item addition, etc.
 * 
 * 3. Utility Integration:
 *    - Uses `getFormatedUnit` to transform units
 *    - Uses `uuidv4` for generating unique item IDs
 *    - Graceful handling of incomplete or missing data via `idx` utility
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.material - List of materials assigned to the sub-task
 * @param {Object} props.total - Total cost summary for all materials
 * @param {Array} props.categoryList - Available catalog categories
 * @param {Array} props.unitList - Full unit list for UoM dropdowns
 * @param {number} props.profitMargin - Project-level profit margin percentage
 * @param {string} props.profitMarginType - Profit margin type (fixed/percentage)
 * @param {boolean} props.isDisabled - Whether the component is in read-only mode
 * @param {Array} props.subTaskArray - Array of all sub-tasks (from Redux)
 * @param {number} props.subTaskArrayIndex - Index of the current sub-task
 * @returns {JSX.Element} Material list view with edit/add support
 */

export default function Material({ material = [], total = {}, categoryList = [], unitList = [], profitMargin, profitMarginType, isDisabled, showReplaceButton = false, onRefreshTemplateList, ...props }) {

    const dispatch = useDispatch()
    // const permissions = useSelector(state => state.user.userData.permissions) || {}
    // const wCatalogPermission = idx(permissions, _ => _.catalog) === 2 ? true : false
    const [materialState, setMaterialState] = useState({
        categoriesList: [],
        itemsList: [],
        items: [],
        filterValue: {},
        item: itemFields,
        profitMarginDialogState: false,
        lockArrayValue: null,
        confirmLock: false,
        showMaterialLockIcon: true,
    })
    const [expandAddMaterial, setExpandAddMaterial] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [suggestedMaterials, setSuggestedMaterials] = useState([]);
    const [materialToReplace, setMaterialToReplace] = useState(null);

    useEffect(() => {
        initLoad()
    }, [])

    const initLoad = () => {
        debouncedFetchItems();
        const allUnits = (unitList && Array.isArray(unitList) && unitList.length > 0 && unitList[0].units) || [];
        const units = getFormatedUnit(allUnits);
        let { item } = materialState;
        item.unit.items = units;
        setMaterialState(prev => ({
            ...prev,
            item
        }));
    }

    const fetchItems = (filter) => {
        let categoryFilter = (filter && filter.catalog) ? filter.catalog : '';
        let categoryId = (filter && filter.catalogid) ? filter.catalogid : '';
        let manufacturerFilter = (filter && filter.manufacturer) ? filter.manufacturer : '';
        let search = (filter && filter.item) ? filter.item : '';
        let partNoFilter = (filter && filter.partNo) ? filter.partNo : '';
        let params = {};
        params = { ...(categoryId ? { categoryId } : { categoryFilter }), manufacturerFilter, search, partNoFilter }
        if (params.categoryId || params.search || params.partNoFilter) {
            dispatch(getInventoryLists(params)).then((items) => {
                const category = items.category;
                setMaterialState(prev => ({
                    ...prev,
                    itemsList: category
                }));
            });
        }
    }

    const debouncedFetchItems = useCallback(
        debounce((filter) => {
            fetchItems(filter)
        }, debounceTime.inventoryList),
        []
    );

    const getCategoriesList = () => {
        return categoryList.map((category) => ({
            key: category.name,
            name: category.name,
            value: category.id,
        }));
    }

    const profitMarginDialogHandler = (inputValue, index) => {
        setMaterialState(prev => ({
            ...prev,
            lockArrayValue: index,
            profitMarginDialogState: !prev.profitMarginDialogState,
        }));
    };

    const itemChangeHandler = (e, index) => {
        let _temp = _.cloneDeep(material)
        switch (e) {
            case "saleAmountLock":
                profitMarginDialogHandler(e, index);
                if (materialState.profitMarginDialogState && materialState.confirmLock) {
                    _temp[index]["saleAmountLock"].value = !_temp[index]["saleAmountLock"].value;
                }
                break;
            default:
                if (e.target.name === "costPerItem" || e.target.name === "qty") {
                    if (e.target.value < 0)
                        break;
                }

                _temp[index][e.target.name].value = e.target.value;

                const is_locked = _temp[index]["saleAmountLock"].value || false;
                let saleAmount = _temp[index]["saleAmount"].value;

                if (e.target.name === "saleAmountLock") {
                    _temp[index]["saleAmountLock"].value = !_temp[index]["saleAmountLock"].value;
                }

                if (e.target.name === "costPerItem" || e.target.name === "qty") {
                    if (saleAmount <= 0 || is_locked) {
                        _temp[index]["saleAmount"].value = _temp[index]["costPerItem"].value
                    }
                }
                saleAmount = _temp[index]["saleAmount"].value;

                _temp[index]["total"].value = (parseFloat(_temp[index]["qty"].value) || 0) * parseFloat(saleAmount);
        }

        updateMaterials(_temp);
    };

    const removeItem = (index) => {
        let _temp = _.cloneDeep(material)
        _temp.splice(index, 1)
        updateMaterials(_temp);
    };

    const updateMaterials = (materials) => {
        if (!Array.isArray(materials)) return;
        const { subTaskArray, subTaskArrayIndex } = props;
        let _temp = _.cloneDeep(subTaskArray)
        _temp[subTaskArrayIndex]["material"] = materials;
        dispatch(updateSubTaskAction(_temp));
    };

    const filterItem = (id) => {
        const item = (materialState.itemsList || []).find((item) => item.id === id);
        return item;
    };

    const addItem = (value, category, itemData = null, skip = false) => {
        let selectedMaterials = _.cloneDeep(material);
        const itemExist = selectedMaterials.find((item) => item.id === value && item.categoryId === category);

        if (!itemExist) {
            const { filterValue } = materialState;
            const selectedItem = itemData ? itemData : filterItem(value);
            if (!category) category = selectedItem.categoryId;
            // if (_.filter(categoryName, { id: category }).map((v) => v.id).length === 0)
            //     this.addCategory(category);

            const item = JSON.parse(JSON.stringify(materialState.item));
            item.name.value = selectedItem.item;
            item.taskId.value = selectedItem.taskId || null;
            item.id = selectedItem.id || uuidv4();
            item.manufacturer = selectedItem.manufacturer;
            item.partNo = selectedItem.partNo || selectedItem.part_no;
            item.categoryId = selectedItem.categoryId;
            item.costPerItem.value = filterValue.costPerItem || selectedItem.cost;
            item.saleAmount.value = (filterValue.costPerItem || filterValue["saleAmountLock"] === false) ?
                (filterValue["saleAmount"] || selectedItem.saleAmount || selectedItem.cost) :
                (selectedItem.saleAmountLock ? selectedItem.cost : selectedItem.saleAmount || selectedItem.cost) || 0;
            item.saleAmountLock.value = filterValue["saleAmountLock"] || selectedItem.saleAmountLock;
            item.unit.value = selectedItem.unit || null;
            item.qty.value = filterValue["qty"] || selectedItem.qty || 1;
            item.total.value = item.saleAmount.value * item.qty.value;

            item.catalog_moved = selectedItem.catalog_moved || false;
            item.different_account = selectedItem.different_account || false;
            item.hide_search = selectedItem.hide_search || false;
            item.status = selectedItem.status || 'ACTIVE';
            item.duplicateStatus = selectedItem.duplicateStatus || false;
            selectedMaterials.push(item);

            updateMaterials(selectedMaterials);
            hideAddItem()
        } else {
            if (!skip) {
                dispatch(warningSnackBar("This Item is Already Selected"));
            }
        }
    };

    const hideAddItem = () => {
        setExpandAddMaterial(!expandAddMaterial)
        setMaterialState(prev => ({
            ...prev,
            filterValue: {}
        }));
    };

    const itemsDropDownOption = () => {
        const { filterValue } = materialState;
        let itemsList = _.cloneDeep(materialState.itemsList)
        let filterOptions = filterItems(itemsList, filterValue);
        if (filterOptions.length === 0) {
            setFilterResult(true, "");
        } else {
            setFilterResult(false, "");
        }
        return filterOptions;
    };
    const setFilterResult = (value, msg) => {
        const { filterValue } = materialState
        let prevValue = idx(filterValue, (_) => _.noresult);
        if (prevValue !== value) {
            filterValue["noresult"] = value;
            filterValue["noresult_msg"] = msg;
            setMaterialState(prev => ({
                ...prev,
                filterValue
            }));
        }
    };

    const addFilter = (value, type) => {
        let { filterValue, showMaterialLockIcon } = materialState;
        if (!filterValue) filterValue = {};
        if (filterValue && filterValue[type] !== value) {
            filterValue[type] = value;
            if (type === "saleAmount" && !showMaterialLockIcon) {
                filterValue["saleAmount"] = value;
                filterValue["saleAmountLock"] = !filterValue["saleAmountLock"];
            }
            if (type === "costPerItem" || type === "qty" || type === "saleAmount") {
                if (showMaterialLockIcon || filterValue["saleAmountLock"])
                    filterValue["saleAmount"] = parseFloat(filterValue["costPerItem"] || 0)

                const totalvalue = (parseInt(filterValue["qty"]) || 1) * (parseFloat(filterValue["saleAmount"]) || 1);

                setMaterialState(prev => ({
                    ...prev,
                    totalValue: parseFloat(totalvalue)
                }));
            }
            setMaterialState(prev => ({
                ...prev,
                filterValue
            }));
            debouncedFetchItems(filterValue)
        }
    };

    const manufacturerDropDown = () => {
        const { filterValue } = materialState;
        let itemsList = _.cloneDeep(materialState.itemsList)
        let filterOptions = filterManufacturer(itemsList, filterValue);
        if (filterOptions.length === 0) {
            setFilterResult(true, "");
        } else {
            setFilterResult(false, "");
        }
        return filterOptions;
    };

    const setConfirmed = () => {
        const { lockArrayValue } = materialState;
        let _selectedItemsList = _.cloneDeep(material);
        _selectedItemsList[lockArrayValue]["saleAmountLock"].value = !_selectedItemsList[lockArrayValue]["saleAmountLock"].value;
        _selectedItemsList.forEach((item, index) => {
            if (item["saleAmountLock"].value) {
                _selectedItemsList[index]["saleAmount"].value = item["costPerItem"].value
                _selectedItemsList[index]["total"].value = (parseInt(item["qty"].value) || 0) * parseFloat(_selectedItemsList[index]["saleAmount"].value);
            }
        });
        setMaterialState(prev => ({
            ...prev,
            profitMarginDialogState: !prev.profitMarginDialogState
        }));
        updateMaterials(_selectedItemsList);
    };

    const partNoDropDown = () => {
        let itemsList = _.cloneDeep(materialState.itemsList)
        let filterOptions = itemsList.map(item => ({
            key: item.partNo || item.part_no,
            value: item.partNo || item.part_no
        }));

        filterOptions = _.uniqBy(filterOptions, 'key');

        return filterOptions;
    };
    const fetchSuggestedMaterials = (material) => {
        const partNo = material?.partNo || '';
        const description = idx(material, _ => _.name.value) || '';
        const itemName = idx(material, _ => _.name.value) || '';

        return dispatch(getSuggestedMaterials({
            item: itemName,
            part_no: partNo,
            item_description: description,
            excludeId: material.id,
        })).then((res) => {
            const suggestedMaterials = res?.suggestedMaterials || [];
            return suggestedMaterials;
        });
    };

    const handleOpenReplaceModal = (material) => {
        setMaterialToReplace(material);
        fetchSuggestedMaterials(material).then((suggestedMaterials) => {
            setSuggestedMaterials(suggestedMaterials);
            setShowReplaceModal(true);
        });
    };



    return (
        <>
            <h2>Material List</h2>
            <div className="table-responsive">
                <Table bordered className="table-create table-material tbl-fixed">
                    <thead>
                        <tr>
                            <th>Catalog</th>
                            <th>Category</th>
                            <th>Part No</th>
                            <th className="item-width">Items Description</th>
                            <th className="cost-width text-right">Unit Cost($)</th>
                            {/* <th>Sale Amount</th> */}
                            <th className="qty-width text-right">Quantity</th>
                            <th className="text-right total-width">Total ($)</th>
                            <th className="text-right action-width">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {material.map((_material, materialIndex) => {
                            if (!_material) return;

                            let category = getCategoriesList().find((item) => item.value === _material.categoryId);
                            return (
                                <tr key={materialIndex}>
                                    <td>{isInlineItem(_material) ? INLINE_CATALOG_DISPLAY_NAME : category?.name || _material.categoryName}</td>
                                    <td>{_material.manufacturer}</td>
                                    <td>{_material.partNo}</td>
                                    <td>{idx(_material, _ => _.name.value) || ""}</td>
                                    <td className="input-text-right cost-width">
                                        <NumberInput
                                            className="input-block-sm"
                                            disabled={isDisabled}
                                            decimalPlaces={2}
                                            {..._material.costPerItem}
                                            onChange={(e) => itemChangeHandler(e, materialIndex)}
                                        />
                                    </td>
                                    {/* <td>
                                        <CustomInput
                                            className="input-block-sm"
                                            disabled={true}
                                            placeholder="Sale Amount"
                                            {..._material.saleAmount}
                                        />
                                    </td> */}
                                    <td className="input-text-right qty-width">
                                        <NumberInput
                                            className={'input-block-sm'}
                                            decimalPlaces={2}
                                            {..._material.qty}
                                            disabled={isDisabled}
                                            onChange={(e) => itemChangeHandler(e, materialIndex)}
                                        />
                                    </td>
                                    <td className="text-right total-width">
                                        ${parseFloat(_material.total.value).toFixed(2) || parseFloat(_material.saleAmount.value).toFixed(2) || 0}
                                    </td>
                                    <td className="text-right action-width">
                                        {!isDisabled && showReplaceButton && (_material.status === 'INACTIVE' || _material.hide_search || _material.different_account || _material.duplicateStatus) && (
                                            <button
                                                data-cy="item-replace"
                                                className="btn btn-outline-primary btn-sm ms-2"
                                                onClick={() => {
                                                    setSelectedIndex(materialIndex);
                                                    handleOpenReplaceModal(_material);
                                                }}
                                            >
                                                Replace
                                            </button>
                                        )}
                                        {!isDisabled && <DeleteIcon
                                            className="deleteicon"
                                            onClick={() => removeItem(materialIndex)}
                                        />}
                                    </td>
                                </tr>
                            )
                        })}

                        <tr>
                            <td colSpan={4}>
                                {!isDisabled && <span
                                    className="add-material"
                                    onClick={() => setExpandAddMaterial(!expandAddMaterial)}
                                    aria-controls="example-collapse-texts"
                                    aria-expanded={expandAddMaterial}
                                >
                                    <AddIcon /> Add Material
                                </span>}
                            </td>
                            <td className="cost-width"></td>
                            <td className="text-right table-text-sm qty-width">Sub Total</td>
                            <td className="text-right table-text-sm total-width">
                                ${parseFloat(idx(total, (_) => _.material.sale) || 0).toFixed(2)}
                            </td>
                            <td className="action-width"></td>
                        </tr>
                    </tbody>
                </Table>
            </div>

            <Collapse in={expandAddMaterial}>
                <div id="example-collapse-texts">
                    <div className="row m-0 mb-2">
                        <div className="col-lg-5 col-md-6 mt-3">
                            <div className="form-group">
                                <div className="input-block autocomplete-block">
                                    <label className="input-label">Catalog</label>
                                    <Autocomplete
                                        disablePortal
                                        id="combo-box-demo"
                                        onChange={(event, newValue) => {
                                            if (typeof newValue === "string") {
                                            } else if (newValue && newValue.inputValue) {
                                            } else if (newValue) {
                                                addFilter(newValue.key, "catalog");
                                                addFilter(newValue.value, "catalogid");
                                            } else {
                                                addFilter("", "catalog");
                                            }
                                        }}
                                        filterOptions={(options, params) => {
                                            return filter(options, params);
                                        }}
                                        selectOnFocus
                                        handleHomeEndKeys
                                        clearOnEscape={false}
                                        clearOnBlur={false}
                                        clearText={true}
                                        options={getCategoriesList()}
                                        getOptionLabel={(option) => {
                                            // e.g value selected with enter, right from the input
                                            if (typeof option === "string") {
                                                return option;
                                            }
                                            return option.key;
                                        }}
                                        onInputChange={(event, newValue) => {
                                            addFilter(newValue.key || newValue, "catalog");
                                            addFilter(newValue.value, "catalogid");
                                        }}
                                        renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Catalog" />}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 mt-3">
                            <div className="form-group">
                                <div className="input-block autocomplete-block">
                                    <label className="input-label">Category</label>
                                    <Autocomplete
                                        disablePortal
                                        id="combo-box-demo"
                                        onChange={(event, newValue) => {
                                            if (typeof newValue === "string") {
                                            } else if (newValue && newValue.inputValue) {
                                            } else if (newValue) {
                                                addFilter(newValue.value, "manufacturer");
                                            } else {
                                                addFilter("", "manufacturer");
                                            }
                                        }}
                                        filterOptions={(options, params) => {
                                            return filter(options, params);
                                        }}
                                        selectOnFocus
                                        handleHomeEndKeys
                                        clearOnEscape={false}
                                        clearOnBlur={false}
                                        clearText={true}
                                        options={manufacturerDropDown()}
                                        getOptionLabel={(option) =>
                                            option.key ? option.key : ""
                                        }
                                        getOptionSelected={(option, value) => {
                                            //nothing that is put in here will cause the warning to go away
                                            if (value === "") {
                                                return true;
                                            } else if (value === option.key) {
                                                return true;
                                            }
                                        }}
                                        onInputChange={(event, newValue) => {
                                            addFilter(newValue, "manufacturer");
                                        }}
                                        renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Category" />}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 mt-3">
                            <div className="form-group">
                                <div className="input-block autocomplete-block">
                                    <label className="input-label">partNo</label>
                                    <Autocomplete
                                        disablePortal
                                        id="combo-box-demo"
                                        onChange={(event, newValue) => {
                                            if (typeof newValue === "string") {
                                            } else if (newValue && newValue.inputValue) {
                                            } else if (newValue) {
                                                addFilter(newValue.value, "partNo");
                                            } else {
                                                addFilter("", "partNo");
                                            }
                                        }}
                                        filterOptions={(options, params) => {
                                            return filter(options, params);
                                        }}
                                        selectOnFocus
                                        handleHomeEndKeys
                                        clearOnEscape={false}
                                        clearOnBlur={false}
                                        clearText={true}
                                        options={partNoDropDown({})}
                                        getOptionLabel={(option) =>
                                            option.key ? option.key : ""
                                        }
                                        getOptionSelected={(option, value) => {
                                            //nothing that is put in here will cause the warning to go away
                                            if (value === "") {
                                                return true;
                                            } else if (value === option.key) {
                                                return true;
                                            }
                                        }}
                                        onInputChange={(event, newValue) => {
                                            addFilter(newValue, "partNo");
                                        }}
                                        renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Part No" />}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5 col-md-12">
                            <div className="row">
                                <div className="col-lg-8 col-md-8 mt-3">
                                    <div className="form-group">
                                        <div className="input-block autocomplete-block">
                                            <label className="input-label">Items</label>
                                            <Autocomplete
                                                disablePortal
                                                id="combo-box-demo"
                                                onChange={(event, newValue) => {
                                                    if (typeof newValue === "string") {
                                                    } else if (newValue && newValue.inputValue) {
                                                        // this.AddItemDialogHandler();
                                                    } else if (newValue) {
                                                        addItem(newValue.value);
                                                    }
                                                }}
                                                selectOnFocus
                                                handleHomeEndKeys
                                                clearOnEscape={false}
                                                clearOnBlur={false}
                                                clearText={true}
                                                onInputChange={(event, newValue) => {
                                                    addFilter(newValue, "item");
                                                }}
                                                options={itemsDropDownOption()}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === "string")
                                                        return option;
                                                    if (option.inputValue)
                                                        return option.inputValue;
                                                    return option.key;
                                                }}
                                                filterOptions={(options, params) => {
                                                    const filtered = filter(options, params);
                                                    if (params.inputValue !== "") {
                                                        filtered.push({
                                                            inputValue: params.inputValue,
                                                            key: `Add "${params.inputValue}"`,
                                                        });
                                                    }
                                                    return filtered;
                                                }}
                                                renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select Item Description" />}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-8 mt-3">
                                    <CustomInput
                                        type="number"
                                        placeholder="Enter unit cost"
                                        formatDecimals={true}
                                        onChange={(e) => {
                                            if (e.target.value >= 0)
                                                addFilter(e.target.value, "costPerItem");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12">
                            <div className="row">
                                <div className="col-lg-6 col-md-6 mt-3">
                                    <CustomInput
                                        type="number"
                                        placeholder="Enter quantity"
                                        formatDecimals={true}
                                        onChange={(e) => {
                                            if (e.target.value >= 0) addFilter(e.target.value, "qty");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 mt-3">
                            <CustomInput
                                label={'Total'}
                                value={(materialState.totalValue || 0).toFixed(2)}
                                disabled={true}
                                formatDecimals={true}
                            />
                        </div>

                        {/* <div className="col-12 d-flex my-3">
                            <Button type="submit" className="secondarybtn secondarybtn-outline btn-sm ms-auto me-3">RESET</Button>
                            <Button type="submit" className="primarybtn btn-sm">ADD</Button>
                        </div> */}
                    </div>


                </div>

            </Collapse>

            <ReplaceMaterialModal
                show={showReplaceModal}
                onClose={() => setShowReplaceModal(false)}
                material={materialToReplace}
                suggestedMaterials={suggestedMaterials}
                onSelect={(replacementMaterial) => {
                    const updated = _.cloneDeep(material);
                    const old = updated[selectedIndex];
                    const cost = parseFloat(replacementMaterial.cost || 0);
                    const qty = parseFloat(old.qty?.value || 1);
                    const saleAmount = parseFloat(replacementMaterial.sale_amount || cost);
                    const unit = replacementMaterial.unit || old.unit?.value || '';
                    const saleAmountLock = !!replacementMaterial.sale_amount_lock;
                    const transformed = {
                        ...old,
                        id: replacementMaterial.id,
                        name: { ...old.name, value: replacementMaterial.item || replacementMaterial.name || '' },
                        partNo: replacementMaterial.part_no || replacementMaterial.partNo || '',
                        categoryId:
                            replacementMaterial.category_id ||
                            (replacementMaterial.category && replacementMaterial.category.id) ||
                            old.categoryId || '',
                        categoryName: replacementMaterial.category?.name || '',
                        manufacturer: replacementMaterial.manufacturer || '',
                        status: replacementMaterial.status || 'ACTIVE',
                        catalog_moved: false,
                        hide_search: false,
                        different_account: false,
                        duplicateStatus: false,
                        costPerItem: { ...old.costPerItem, value: cost },
                        qty: { ...old.qty, value: qty },
                        saleAmount: { ...old.saleAmount, value: saleAmount },
                        saleAmountLock: { ...old.saleAmountLock, value: saleAmountLock },
                        unit: { ...old.unit, value: unit },
                        total: { ...old.total, value: cost * qty },
                    };
                    updated[selectedIndex] = transformed;
                    updateMaterials(updated);
                    setShowReplaceModal(false);

                    // Refresh template list after successful replace
                    if (onRefreshTemplateList && typeof onRefreshTemplateList === 'function') {
                        onRefreshTemplateList();
                    }
                }}
            />
            <ProfitMarginLock
                open={materialState.profitMarginDialogState}
                lockIconChange={() => setConfirmed()}
                dialogClose={profitMarginDialogHandler}
                moduleName="Task"
                modalHeading={"Sale Amount Lock changes"}
                text={"sale Amount"}
                message={'Are you sure you want to change the Project profit margin? - Any items using default profit margins will also be updated to this value'}
            />
        </>
    );

}
